/**
 * division-registry — pure division-scoped visibility logic (frontend port).
 *
 * This is a faithful frontend port of the pure helper that lives in the infra
 * Lambda at `infrastructure/lambda/division-registry/visibility.ts` (spec task
 * 4.2). The semantics are IDENTICAL — it is duplicated here only because the
 * frontend build (Vite) cannot import from the infrastructure package and there
 * is no shared package wired into the frontend. Keep the two in sync.
 *
 * Generalizes the legacy `ApplicantTypeVisibility { MRS | MISS | BOTH }` enum
 * into a data-driven selection over the configured Divisions. The wire shape is
 * a nullable `visibleDivisionKeys: [String!]`:
 *
 *   - null / empty list  ⇒ visible for ALL active Divisions (the `BOTH`
 *     semantics — Req 5.3, 5.5)
 *   - populated list      ⇒ visible only for the listed Division keys (Req 5.4)
 *
 * `legacyVisibilityToDivisionKeys` read-through-maps existing
 * `applicantTypeVisibility` enum values onto the new shape
 * (`MRS → ["mrs"]`, `MISS → ["miss"]`, `BOTH → all`) so existing `FormField`
 * rows keep working with no data migration.
 *
 * This module is PURE — no I/O.
 *
 * Validates: Requirements 5.3, 5.4, 5.5
 */

import type { ApplicantType } from '@/types';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * The legacy GraphQL enum `ApplicantTypeVisibility { MRS | MISS | BOTH }` used
 * to scope a UI element to a single division or to all divisions.
 */
export type ApplicantTypeVisibility = ApplicantType | 'BOTH';

/**
 * The minimal Division shape this module reads for the all-divisions case.
 * Structurally a subset of the `Division` type in `@/types`.
 */
export interface DivisionVisibilityInfo {
  key: string;
  active: boolean;
}

// -----------------------------------------------------------------------------
// Back-Compat mapping (mirrors infra `resolution.ts`)
// -----------------------------------------------------------------------------

/**
 * Total mapping from the `ApplicantType` enum to its Back-Compat Division key,
 * matching the `mrs`/`miss` Divisions the migration seeds into the registry.
 */
export const backCompatMapping: Readonly<Record<ApplicantType, string>> = {
  MRS: 'mrs',
  MISS: 'miss',
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Whether a `visibleDivisionKeys` selection means "all divisions" — i.e. it is
 * null, undefined, or empty (the `BOTH` semantics, Req 5.3/5.5). Whitespace-only
 * entries are ignored so a blank value never accidentally scopes the element.
 */
export function isAllDivisionsVisibility(
  visibleDivisionKeys: readonly string[] | null | undefined,
): boolean {
  if (visibleDivisionKeys == null) {
    return true;
  }
  return visibleDivisionKeys.every((k) => typeof k !== 'string' || k.trim() === '');
}

/** The Division keys that are currently active, derived from a division list. */
function activeDivisionKeys(
  activeDivisions: readonly DivisionVisibilityInfo[],
): Set<string> {
  const keys = new Set<string>();
  for (const division of activeDivisions) {
    if (division.active) {
      keys.add(division.key);
    }
  }
  return keys;
}

// -----------------------------------------------------------------------------
// Visibility
// -----------------------------------------------------------------------------

/**
 * Whether a UI element carrying the given `visibleDivisionKeys` selection is
 * visible for the Division identified by `divisionKey`.
 *
 *   - When `visibleDivisionKeys` is null/empty, the element is visible for every
 *     active Division (the `BOTH` semantics): it is visible for `divisionKey`
 *     iff that key belongs to an active Division (Req 5.3, 5.5).
 *   - When `visibleDivisionKeys` is populated, the element is visible only for
 *     the listed keys: it is visible for `divisionKey` iff the list contains it
 *     (Req 5.4).
 */
export function isVisibleForDivision(
  visibleDivisionKeys: readonly string[] | null | undefined,
  divisionKey: string,
  activeDivisions: readonly DivisionVisibilityInfo[],
): boolean {
  if (isAllDivisionsVisibility(visibleDivisionKeys)) {
    return activeDivisionKeys(activeDivisions).has(divisionKey);
  }
  // Populated list: scoped to exactly the listed keys (Req 5.4).
  return (visibleDivisionKeys as readonly string[]).some(
    (k) => typeof k === 'string' && k.trim() !== '' && k === divisionKey,
  );
}

// -----------------------------------------------------------------------------
// Legacy enum read-through mapping (Req 5.3)
// -----------------------------------------------------------------------------

/**
 * Map a legacy `ApplicantTypeVisibility` enum value onto the new
 * `visibleDivisionKeys` shape so existing rows keep working during the
 * transition:
 *
 *   - `MRS`  → `["mrs"]`  (scoped to the Back-Compat `mrs` Division)
 *   - `MISS` → `["miss"]` (scoped to the Back-Compat `miss` Division)
 *   - `BOTH` → `null`     (all divisions)
 *
 * Returning `null` for `BOTH` yields the all-divisions semantics when fed to
 * {@link isVisibleForDivision}.
 */
export function legacyVisibilityToDivisionKeys(
  visibility: ApplicantTypeVisibility,
): string[] | null {
  if (visibility === 'BOTH') {
    return null;
  }
  return [backCompatMapping[visibility]];
}
